import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ToolLibraryRecord } from '@bridgewerk/shared';
import { Skeleton } from '../../../components/ui/Skeleton.js';
import { useToast } from '../../../components/ui/Toast.js';
import { useSwipeDown } from '../../../hooks/useSwipeDown.js';
import { useDeleteToolLibraryEntry, useGraveyard, useToolAssignments, type ToolAssignment } from '../hooks/useToolLibrary.js';
import { EditToolModal } from './EditToolModal.js';

export interface ToolLibraryTableProps {
  readonly shopId: string | null;
  readonly entries: readonly ToolLibraryRecord[] | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly onRetry: () => void;
}

type SortKey = 'date' | 'alpha' | 'material';

function sortEntries(entries: readonly ToolLibraryRecord[], sort: SortKey): ToolLibraryRecord[] {
  const arr = [...entries];
  switch (sort) {
    case 'alpha':
      return arr.sort((a, b) => a.description.localeCompare(b.description));
    case 'material':
      return arr.sort((a, b) =>
        a.material.localeCompare(b.material) || a.description.localeCompare(b.description),
      );
    case 'date':
    default:
      return arr.sort((a, b) => b.created_at.localeCompare(a.created_at));
  }
}

export function ToolLibraryTable({
  shopId,
  entries,
  isLoading,
  isError,
  onRetry,
}: ToolLibraryTableProps): JSX.Element {
  const [sort, setSort]       = useState<SortKey>('date');
  const [selected, setSelected] = useState<ToolLibraryRecord | null>(null);
  const [editing, setEditing]   = useState<ToolLibraryRecord | null>(null);

  const sorted = useMemo(
    () => (entries ? sortEntries(entries, sort) : []),
    [entries, sort],
  );

  if (isLoading) {
    return (
      <div>
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="px-4 py-3 border-b border-border last:border-0">
            <Skeleton className="h-4 w-2/3 mb-1.5" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-4 py-6 text-center text-xs text-text-dim font-mono">
        <p>Failed to load tool library.</p>
        <button onClick={onRetry} className="mt-2 text-accent underline">Retry</button>
      </div>
    );
  }

  if (!entries || entries.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-xs text-text-dim font-mono">
        <p>No tools in the library yet.</p>
        <p className="mt-1 opacity-60">Add a tool above to track identity for slot assignments.</p>
      </div>
    );
  }

  return (
    <>
      {/* Sort bar */}
      <div className="flex items-center gap-1 px-4 py-2.5 border-b border-border">
        <span className="text-[9px] font-mono uppercase tracking-widest text-muted mr-1.5">Sort</span>
        {([['date', 'Newest'], ['alpha', 'A–Z'], ['material', 'Material']] as [SortKey, string][]).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setSort(key)}
            className={[
              'px-2.5 py-1 rounded text-[10px] font-mono uppercase tracking-widest transition-colors',
              sort === key
                ? 'bg-accent/15 text-accent border border-accent/30'
                : 'text-text-dim hover:text-text border border-transparent',
            ].join(' ')}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tool rows */}
      <div className="divide-y divide-border">
        {sorted.map(entry => (
          <ToolRow key={entry.tool_id} entry={entry} onClick={() => setSelected(entry)} />
        ))}
      </div>

      {/* Detail sheet (portalled so it escapes any overflow/z-index container) */}
      {selected && shopId && (
        <ToolDetailSheet
          entry={selected}
          shopId={shopId}
          onEdit={() => { setEditing(selected); setSelected(null); }}
          onDeleted={() => setSelected(null)}
          onClose={() => setSelected(null)}
        />
      )}

      {editing && shopId && (
        <EditToolModal shopId={shopId} tool={editing} onClose={() => setEditing(null)} />
      )}
    </>
  );
}

// ── Tool row ──────────────────────────────────────────────────────────────────

function ToolRow({
  entry,
  onClick,
}: {
  readonly entry: ToolLibraryRecord;
  readonly onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-card/60 active:bg-card/80 transition-colors group"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-mono truncate text-text">{entry.description}</p>
        <p className="text-[10px] font-mono text-text-dim mt-0.5 truncate">
          {entry.type} · {entry.material}
          {entry.diameter_mm != null ? ` · ${entry.diameter_mm} mm` : ''}
          {entry.manufacturer ? ` · ${entry.manufacturer}` : ''}
        </p>
      </div>
      {entry.part_number && (
        <span className="shrink-0 text-[10px] font-mono text-muted hidden sm:block">
          {entry.part_number}
        </span>
      )}
      <span className="shrink-0 text-text-dim group-hover:text-text transition-colors text-sm">›</span>
    </button>
  );
}

// ── Tool detail sheet ─────────────────────────────────────────────────────────

function ToolDetailSheet({
  entry,
  shopId,
  onEdit,
  onDeleted,
  onClose,
}: {
  readonly entry: ToolLibraryRecord;
  readonly shopId: string;
  readonly onEdit: () => void;
  readonly onDeleted: () => void;
  readonly onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const del = useDeleteToolLibraryEntry(shopId);
  const assignments = useToolAssignments(shopId, entry.tool_id);
  const graveyard   = useGraveyard(shopId);
  const toolHistory = (graveyard.data ?? []).filter(g => g.tool_library_id === entry.tool_id);
  const [confirming, setConfirming] = useState(false);
  const swipe = useSwipeDown(onClose);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleDelete = async () => {
    try {
      await del.mutateAsync(entry.tool_id);
      toast.push({ tone: 'success', title: 'Tool removed', description: entry.description });
      onDeleted();
    } catch (err) {
      toast.push({
        tone: 'critical',
        title: 'Could not remove',
        description: err instanceof Error ? err.message : String(err),
      });
      setConfirming(false);
    }
  };

  const fields: Array<[string, string]> = [
    ['Type', entry.type],
    ['Material', entry.material],
    ...(entry.diameter_mm != null       ? [['Diameter', `${entry.diameter_mm} mm`] as [string, string]] : []),
    ...(entry.manufacturer              ? [['Manufacturer', entry.manufacturer] as [string, string]] : []),
    ...(entry.part_number               ? [['Part No.', entry.part_number] as [string, string]] : []),
    ...(entry.expected_life_cuts != null    ? [['Life (cuts)', String(entry.expected_life_cuts)] as [string, string]] : []),
    ...(entry.expected_life_minutes != null ? [['Life (min)', String(entry.expected_life_minutes)] as [string, string]] : []),
    ...(entry.notes                     ? [['Notes', entry.notes] as [string, string]] : []),
    ['Added', new Date(entry.created_at).toLocaleDateString()],
  ];

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.65)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative w-full md:max-w-md rounded-t-2xl md:rounded-xl border-t md:border border-border overflow-hidden flex flex-col"
        style={{
          backgroundColor: 'rgba(11,16,23,0.98)',
          animation: 'sheet-up 0.32s cubic-bezier(0.32,0.72,0,1)',
          maxHeight: '82vh',
          paddingBottom: 'env(safe-area-inset-bottom)',
          ...swipe.style,
        }}
      >
        {/* Drag handle (mobile) — swipe down to dismiss */}
        <div
          className="flex justify-center pt-3 pb-1 md:hidden shrink-0 touch-none cursor-grab"
          {...swipe.handlers}
        >
          <div className="h-1 w-10 rounded-full bg-border" />
        </div>

        {/* Header */}
        <div className="px-5 py-3 border-b border-border flex items-start justify-between shrink-0">
          <p className="text-sm font-mono text-text font-semibold leading-snug flex-1 pr-4 min-w-0 truncate">
            {entry.description}
          </p>
          <button type="button" onClick={onClose} className="text-text-dim hover:text-text shrink-0 leading-none">✕</button>
        </div>

        {/* Fields */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
            {fields.map(([label, value]) => (
              <div key={label} className={label === 'Notes' ? 'col-span-2' : ''}>
                <dt className="text-[9px] font-mono uppercase tracking-widest text-muted">{label}</dt>
                <dd className="text-sm font-mono text-text mt-0.5 break-words leading-snug">{value}</dd>
              </div>
            ))}
          </dl>

          {/* In-use section */}
          <div>
            <p className="text-[9px] font-mono uppercase tracking-widest text-muted mb-2">In use</p>
            {assignments.isLoading && (
              <p className="text-xs font-mono text-text-dim">Loading…</p>
            )}
            {assignments.isError && (
              <p className="text-xs font-mono text-red/70">Failed to load assignments.</p>
            )}
            {!assignments.isLoading && !assignments.isError && (!assignments.data || assignments.data.length === 0) && (
              <p className="text-xs font-mono text-text-dim">Not assigned to any machine.</p>
            )}
            {assignments.data && assignments.data.length > 0 && (
              <div className="space-y-2">
                {assignments.data.map(a => (
                  <AssignmentRow key={`${a.machine_id}-${a.tool_number}`} assignment={a} />
                ))}
              </div>
            )}
          </div>
          {/* Retired from section */}
          {toolHistory.length > 0 && (
            <div>
              <p className="text-[9px] font-mono uppercase tracking-widest text-muted mb-2">Retired from</p>
              <div className="space-y-2">
                {toolHistory.map(g => (
                  <div key={g.graveyard_id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 opacity-60">
                    <span className="shrink-0 text-[11px] font-mono font-semibold text-text-dim w-8">
                      T{String(g.tool_number).padStart(2, '0')}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-mono text-text-dim truncate">{g.machine_id}</p>
                      <p className="text-[9px] font-mono text-muted">{g.life_percent_at_removal}% life remaining</p>
                    </div>
                    <span className="text-[9px] font-mono text-muted shrink-0">
                      {new Date(g.removed_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="shrink-0 px-5 py-4 border-t border-border flex gap-2">
          <button
            type="button"
            onClick={onEdit}
            className="flex-1 rounded-md bg-accent/10 border border-accent/30 py-2.5 text-sm font-mono text-accent hover:bg-accent/20 transition-colors"
          >
            Edit
          </button>
          {confirming ? (
            <>
              <button
                type="button"
                onClick={() => void handleDelete()}
                disabled={del.isPending}
                className="flex-1 rounded-md bg-red/15 border border-red/30 py-2.5 text-sm font-mono text-red hover:bg-red/25 transition-colors disabled:opacity-50"
              >
                {del.isPending ? 'Removing…' : 'Confirm delete'}
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="px-4 rounded-md border border-border py-2.5 text-sm font-mono text-text-dim hover:text-text transition-colors"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="flex-1 rounded-md border border-border py-2.5 text-sm font-mono text-text-dim hover:text-red hover:border-red/40 transition-colors"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Assignment row ────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  change: 'text-red border-red/30 bg-red/10',
  order:  'text-amber border-amber/30 bg-amber/10',
  watch:  'text-yellow border-yellow/30 bg-yellow/10',
  good:   'text-green border-green/30 bg-green/10',
};

function AssignmentRow({ assignment }: { readonly assignment: ToolAssignment }): JSX.Element {
  const pocketLabel = `T${String(assignment.tool_number).padStart(2, '0')}`;
  const assignedAt = assignment.assigned_at
    ? new Date(assignment.assigned_at).toLocaleDateString()
    : '—';
  const statusColor = STATUS_COLORS[assignment.status] ?? 'text-text-dim border-border bg-transparent';

  return (
    <div className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5">
      <span className="shrink-0 text-[11px] font-mono font-semibold text-text w-8">{pocketLabel}</span>

      {/* Life bar */}
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-mono text-text-dim truncate mb-1">{assignment.machine_id}</p>
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 rounded-full bg-border overflow-hidden">
            <div
              className="h-full rounded-full bg-accent transition-all"
              style={{ width: `${Math.min(100, Math.max(0, assignment.life_percent))}%` }}
            />
          </div>
          <span className="shrink-0 text-[10px] font-mono text-text-dim w-8 text-right">
            {assignment.life_percent}%
          </span>
        </div>
      </div>

      <div className="shrink-0 flex flex-col items-end gap-1">
        <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-widest border ${statusColor}`}>
          {assignment.status}
        </span>
        <span className="text-[9px] font-mono text-muted">since {assignedAt}</span>
      </div>
    </div>
  );
}
